import { useState } from "react";
import {
  Check,
  ImageIcon,
  X,
  Wifi,
  Snowflake,
  Fan,
  Tv,
  Refrigerator,
  Flame,
  Microwave,
  Blend,
  Coffee,
  Sandwich,
  ChefHat,
  UtensilsCrossed,
  Utensils,
  BedDouble,
  Sofa,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useI18n } from "../lib/i18n";

const AMENITY_ICONS = {
  ac: Snowflake,
  ac_each: Snowflake,
  ceiling_fan: Fan,
  ceiling_fan_each: Fan,
  wall_fan: Fan,
  smart_tv: Tv,
  smart_tv_each: Tv,
  smart_cable_tv: Tv,
  wifi: Wifi,
  fridge: Refrigerator,
  gas_stove_oven: Flame,
  gas_stove_2: Flame,
  microwave: Microwave,
  blender: Blend,
  kettle: Coffee,
  toaster: Sandwich,
  air_fryer: ChefHat,
  full_kitchen: UtensilsCrossed,
  kitchen_equipped: UtensilsCrossed,
  kitchenware: Utensils,
  linens: BedDouble,
  sofa_bed: Sofa,
};

const ACCOMMODATION_AMENITIES = [
  // Casa 2 camere
  [
    "ac_each",
    "ceiling_fan_each",
    "smart_tv_each",
    "wifi",
    "kitchen_equipped",
    "ceiling_fan",
    "fridge",
    "gas_stove_oven",
    "microwave",
    "blender",
    "kettle",
    "toaster",
    "linens",
  ],
  // Casa 1 camera
  [
    "ac",
    "smart_cable_tv",
    "wifi",
    "ceiling_fan",
    "wall_fan",
    "fridge",
    "gas_stove_2",
    "kitchenware",
    "air_fryer",
    "kettle",
    "linens",
  ],
  // Appartamento 1 camera
  [
    "ac",
    "smart_cable_tv",
    "wifi",
    "full_kitchen",
    "gas_stove_oven",
    "fridge",
    "kettle",
    "blender",
    "toaster",
    "kitchenware",
    "sofa_bed",
    "ceiling_fan",
    "linens",
  ],
];

// Casa 2 camere - foto reali fornite dal proprietario
const CASA_2BR_IMAGES = [
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/ibk62j2u_1.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/62sc422y_6.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/gdx475ux_18.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/m2x3vzhx_3.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/0seka0wd_5.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/cmdrb5yo_23.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/ze0w8v2t_10.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/gumej1o6_24.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/5udh3tu5_15.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/uoueseo9_13.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/uzny0wur_9.jpeg",
];

// Casa 1 camera - foto reali fornite dal proprietario
// Ordine: Esterno cancello → Veranda tavolino → Camera wide → Camera (2 letti) → Camera Bob Marley → Bagno wide → Bagno close-up → Angolo cottura → Veranda con cucinetta esterna
const CASA_1BR_IMAGES = [
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/2719ozzj_65.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/etpmlg55_WhatsApp%20Image%202026-06-27%20at%2015.03.45.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/umdx9cg7_55.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/4owz1aap_69.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/e9v93n5b_64.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/4n0fdvpm_49.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/tl3q01y7_62.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/w7hz176m_63.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/pacnsh2w_66.jpeg",
];

// Appartamento 1 camera - foto reali fornite dal proprietario
// Ordine richiesto: Soggiorno+cucina → Camera con leone → Dettaglio cassettiera → Cucina close-up → Cucina dettaglio → Bagno wide → Bagno dettaglio → Angolo divano → Corridoio/ingresso → Veranda
const APARTMENT_1BR_IMAGES = [
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/lfdlempa_39.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/7g69i7o1_31.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/t2z0a4s7_35.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/217p9htx_37.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/c4bl3p3k_28.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/h1ila5uu_27.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/u2as5xyj_26.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/ozv2vogi_34.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/fxzn7dhz_40.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/i9pnkltj_29.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/vsn9z2sg_38.jpeg",
];

const ACCOMMODATION_IMAGES = [
  CASA_2BR_IMAGES,
  CASA_1BR_IMAGES,
  APARTMENT_1BR_IMAGES,
];

const CAROUSEL_OPTS = { loop: true };

export const Gallery = () => {
  const { t, lang } = useI18n();
  const g = t.gallery;
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-white py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <span className="inline-block text-[#FFB800] font-bold tracking-[0.25em] text-xs md:text-sm uppercase">
            {g.kicker}
          </span>
          <h2
            data-testid="gallery-title"
            className="mt-4 text-[#1A1A1A] font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
          >
            {g.title}
          </h2>
          <p className="mt-5 text-[#4A4A4A] text-base sm:text-lg leading-relaxed">
            {g.lead}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {g.items.map((item, i) => {
            const images = ACCOMMODATION_IMAGES[i] || [];
            const hasMultiple = images.length > 1;
            return (
              <article
                key={i}
                data-testid={`accommodation-card-${i}`}
                className="group rounded-3xl overflow-hidden bg-[#FAFAFA] border border-black/5 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              >
                <button
                  type="button"
                  onClick={() => hasMultiple && setOpenIndex(i)}
                  data-testid={`accommodation-photos-${i}`}
                  className={`relative aspect-[4/3] overflow-hidden block w-full text-left ${
                    hasMultiple ? "cursor-pointer" : "cursor-default"
                  }`}
                  aria-label={`${item.name} — ${images.length} ${
                    images.length === 1 ? g.photo_singular : g.photo_plural
                  }`}
                >
                  <img
                    src={images[0]}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FFB800] text-[#1A1A1A] font-black text-sm shadow-md">
                    {item.tag}
                  </div>
                  {hasMultiple && (
                    <div className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md text-white text-xs font-semibold">
                      <ImageIcon className="w-3.5 h-3.5" />
                      {images.length} {g.photo_plural}
                    </div>
                  )}
                </button>
                <div className="p-6 md:p-7">
                  <h3
                    className="text-xl md:text-2xl font-bold text-[#1A1A1A] leading-tight"
                    style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
                  >
                    {item.name}
                  </h3>
                  <p className="mt-3 text-[#4A4A4A] text-sm md:text-base leading-relaxed">
                    {item.desc}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {item.features.map((f, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-2 text-sm text-[#1A1A1A]"
                      >
                        <Check className="w-4 h-4 mt-0.5 text-[#2D5A27] shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-6 border-t border-black/5">
                    <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#FFB800] mb-3">
                      {g.amenities_title}
                    </div>
                    <ul className="flex flex-wrap gap-2">
                      {(ACCOMMODATION_AMENITIES[i] || []).map((key) => {
                        const Icon = AMENITY_ICONS[key] || Check;
                        const label = g.amenities?.[key];
                        if (!label) return null;
                        return (
                          <li
                            key={key}
                            data-testid={`amenity-${i}-${key}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F1E7] text-[#1A1A1A] px-3 py-1.5 text-xs font-medium border border-black/5"
                          >
                            <Icon className="w-3.5 h-3.5 text-[#2D5A27]" />
                            <span>{label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {hasMultiple && (
                    <button
                      type="button"
                      onClick={() => setOpenIndex(i)}
                      data-testid={`view-photos-${i}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] hover:text-[#FFB800] transition-colors"
                    >
                      <ImageIcon className="w-4 h-4" />
                      {g.view_photos}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <Lightbox
        open={openIndex !== null}
        onClose={() => setOpenIndex(null)}
        images={openIndex !== null ? ACCOMMODATION_IMAGES[openIndex] : []}
        title={openIndex !== null ? g.items[openIndex]?.name : ""}
        lang={lang}
      />
    </section>
  );
};

const Lightbox = ({ open, onClose, images, title, lang }) => {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="photo-lightbox"
        className="max-w-5xl p-0 overflow-hidden bg-[#1A1A1A] border-none [&>button]:hidden"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="relative">
          <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
            <span className="text-white/95 font-semibold text-sm md:text-base bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              data-testid="lightbox-close"
              aria-label={lang === "it" ? "Chiudi" : "Close"}
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-[#FFB800] hover:text-[#1A1A1A] grid place-items-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <Carousel className="w-full" opts={CAROUSEL_OPTS}>
            <CarouselContent>
              {images.map((src, idx) => (
                <CarouselItem key={idx}>
                  <div className="aspect-[4/3] md:aspect-[16/10] w-full bg-black flex items-center justify-center">
                    <img
                      src={src}
                      alt={`${title} ${idx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-3 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-[#FFB800] hover:text-[#1A1A1A]" />
            <CarouselNext className="right-3 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-[#FFB800] hover:text-[#1A1A1A]" />
          </Carousel>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Gallery;
